import { GRID_MAX_DIST, GRID_MIN_DIST, GRID_RES } from "./constants";
import { getGroupAlignmentAxes, getGroupGridSearchSpace, projectToLocal, } from "./gridSearchSpace";
/** Per-foundation local bounding box, projected onto the group's alignment axes. */
export function getFoundationLocalBBoxes(foundations, axes) {
    const result = new Map();
    for (const foundation of foundations) {
        let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
        for (const point of foundation.points) {
            const [localX, localY] = projectToLocal(point, axes);
            minX = Math.min(minX, localX);
            maxX = Math.max(maxX, localX);
            minY = Math.min(minY, localY);
            maxY = Math.max(maxY, localY);
        }
        result.set(foundation.id, { minX, maxX, minY, maxY });
    }
    return result;
}
/**
 * Candidate grid line positions spanning a search range, spaced GRID_RES apart.
 * Computed as `min + i * GRID_RES` (rather than repeated addition) to avoid
 * floating-point drift accumulating over many steps.
 */
function generateLinePositions(range) {
    const steps = Math.floor((range.max - range.min) / GRID_RES);
    return Array.from({ length: steps + 1 }, (_, i) => range.min + i * GRID_RES);
}
/**
 * True if foundation `b` blocks the view between a grid line and foundation
 * `a` — i.e. their extents along the cross axis overlap within GRID_MAX_DIST.
 * Checked bidirectionally: not blocking if `a` is entirely past `b` on either side.
 */
function isBlocking(a, b) {
    const aClearsAbove = a.crossMin - GRID_MAX_DIST > b.crossMax + GRID_MAX_DIST;
    const bClearsAbove = b.crossMin - GRID_MAX_DIST > a.crossMax + GRID_MAX_DIST;
    return !aClearsAbove && !bClearsAbove;
}
/**
 * Sweeps `lines` along one axis, tracking near/blocked/far membership per
 * foundation extent, and returns one coverage record per line.
 */
function sweepAxis(lines, extents) {
    const minList = [...extents].sort((a, b) => a.min - b.min);
    const maxList = [...extents].sort((a, b) => a.max - b.max);
    const active = new Map();
    let addIdx = 0;
    let removeIdx = 0;
    const coverage = [];
    for (const position of lines) {
        // Bring foundations into consideration once the line is within GRID_MAX_DIST of their near side.
        while (addIdx < minList.length && minList[addIdx].min - GRID_MAX_DIST <= position) {
            const extent = minList[addIdx];
            active.set(extent.id, extent);
            addIdx++;
        }
        // Drop foundations once the line has passed beyond GRID_MAX_DIST of their far side.
        while (removeIdx < maxList.length && maxList[removeIdx].max + GRID_MAX_DIST < position) {
            active.delete(maxList[removeIdx].id);
            removeIdx++;
        }
        const near = [];
        const blocked = [];
        const far = [];
        for (const extent of active.values()) {
            if (position >= extent.min - GRID_MIN_DIST && position <= extent.max + GRID_MIN_DIST) {
                blocked.push(extent);
            }
            else if (position < extent.min - GRID_MIN_DIST) {
                near.push(extent);
            }
            else {
                far.push(extent);
            }
        }
        // A near/far claim only counts if no blocked foundation obstructs the line of sight.
        const filterBlocked = (candidates) => blocked.length === 0
            ? candidates
            : candidates.filter((a) => !blocked.some((b) => isBlocking(a, b)));
        coverage.push({
            position,
            near: filterBlocked(near).map((e) => e.id),
            blocked: blocked.map((e) => e.id),
            far: filterBlocked(far).map((e) => e.id),
        });
    }
    return coverage;
}
/**
 * Computes, for a group of aligned foundations, the near/blocked/far
 * coverage state of every candidate grid line along both the local x axis
 * and the local y axis.
 */
export function computeGridLineCoverage(group) {
    const axes = getGroupAlignmentAxes(group);
    const { xRange, yRange } = getGroupGridSearchSpace(group);
    const bboxes = getFoundationLocalBBoxes(group.foundations, axes);
    const xExtents = [];
    const yExtents = [];
    for (const foundation of group.foundations) {
        const bbox = bboxes.get(foundation.id);
        xExtents.push({ id: foundation.id, min: bbox.minX, max: bbox.maxX, crossMin: bbox.minY, crossMax: bbox.maxY });
        yExtents.push({ id: foundation.id, min: bbox.minY, max: bbox.maxY, crossMin: bbox.minX, crossMax: bbox.maxX });
    }
    return {
        x: sweepAxis(generateLinePositions(xRange), xExtents),
        y: sweepAxis(generateLinePositions(yRange), yExtents),
    };
}
